import { AxiosResponse } from "axios";
import axiosInstance from "../../../middlewares/axiosInstance";
import {
  StockValuationRequest,
  StockValuationResponse,
  APIResponse,
  StockValuationFilters,
} from "../types/stock-valuation.types";

class StockValuationService {
  private baseUrl = "/business-admin/stock-valuation";

  /**
   * Get stock valuation data with filtering and pagination
   */
  async getStockValuation(
    request: StockValuationRequest = {}
  ): Promise<AxiosResponse<APIResponse<StockValuationResponse>>> {
    const requestBody = {
      page: request.page || 1,
      page_size: request.page_size || 20,
      filters: request.filters || {},
      sort_field: request.sort_field || "total_value",
      sort_order: request.sort_order || "desc",
    };

    return axiosInstance.post(`${this.baseUrl}/list`, requestBody);
  }

  /**
   * Get stock valuation summary only
   */
  async getStockValuationSummary(
    filters: StockValuationFilters = {}
  ): Promise<AxiosResponse<APIResponse<any>>> {
    return axiosInstance.post(`${this.baseUrl}/summary`, { filters });
  }

  /**
   * Get recent stock valuation (current day)
   */
  async getCurrentStockValuation(
    pageSize: number = 20
  ): Promise<AxiosResponse<APIResponse<StockValuationResponse>>> {
    return this.getStockValuation({
      page: 1,
      page_size: pageSize,
      sort_field: "total_value",
      sort_order: "desc",
    });
  }

  /**
   * Get valuation for a specific product
   */
  async getProductValuation(
    productId: string
  ): Promise<AxiosResponse<APIResponse<StockValuationResponse>>> {
    return this.getStockValuation({
      page: 1,
      page_size: 100,
      filters: {
        product_id: productId,
      },
      sort_field: "aging_days",
      sort_order: "desc",
    });
  }

  /**
   * Get expired stock items
   */
  async getExpiredStock(
    pageSize: number = 50
  ): Promise<AxiosResponse<APIResponse<StockValuationResponse>>> {
    return this.getStockValuation({
      page: 1,
      page_size: pageSize,
      filters: {
        only_expired: true,
      },
      sort_field: "aging_days",
      sort_order: "desc",
    });
  }

  /**
   * Get low stock items
   */
  async getLowStockItems(
    pageSize: number = 50
  ): Promise<AxiosResponse<APIResponse<StockValuationResponse>>> {
    return this.getStockValuation({
      page: 1,
      page_size: pageSize,
      filters: {
        only_low_stock: true,
      },
      sort_field: "quantity_available",
      sort_order: "asc",
    });
  }

  /**
   * Get aging analysis
   */
  async getAgingAnalysis(
    warehouseLocation?: string
  ): Promise<AxiosResponse<APIResponse<StockValuationResponse>>> {
    return this.getStockValuation({
      page: 1,
      page_size: 1000,
      filters: {
        warehouse_location: warehouseLocation,
      },
      sort_field: "aging_days",
      sort_order: "desc",
    });
  }

  /**
   * Export stock valuation data
   */
  async exportStockValuation(
    format: "excel" | "csv",
    filters: StockValuationFilters = {}
  ): Promise<AxiosResponse<Blob>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/export`,
      {
        format,
        filters,
      },
      {
        responseType: "blob",
      }
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `stock-valuation-${new Date().toISOString().split("T")[0]}.${format}`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response;
  }
}

export const stockValuationService = new StockValuationService();
